// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import {
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogProps,
    DialogSurface,
    Dropdown,
    Field,
    Input,
    Option,
} from '@fluentui/react-components';
import { CoursePlatform, isNonEmptyString, isNullOrEmptyString, validate } from '@microsoft/vscodeedu-api';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { MaxTitleLength } from '../../models';
import { useAppStyles } from '../../styles';
import { getCoursePlatformText, IntlProps, TrackContext } from '../common';
import { CancelButton, ConfirmButton, DialogHeader } from '../dialogs';

const repoNamePrefix = /^http(s)?:\/\/github.com\//i;

// Link existing repo dialog props.
export type LinkRepoDialogProps = IntlProps<
    Partial<DialogProps> & {
        onLink: (repoName: string, platform: CoursePlatform) => void;
    }
>;

// Link existing repo dialog.
export const LinkRepoDialog = injectIntl((props: LinkRepoDialogProps) => {
    const { intl, onLink, open } = props;
    const appStyles = useAppStyles();
    const [repoName, setRepoName] = useState('');
    const [repoNameError, setRepoNameError] = useState<string | undefined>(undefined);
    const [platform, setPlatform] = useState<CoursePlatform>('python');

    // Reset state on reopening the dialog.
    useEffect(() => {
        if (open === true) {
            setRepoName('');
            setPlatform('python');
        }
    }, [open]);

    useEffect(() => {
        if (isNullOrEmptyString(repoName)) {
            setRepoNameError(undefined);
        } else {
            const value = repoName.replace(repoNamePrefix, '');
            setRepoNameError(validate.githubRepo(value, 'Value'));
        }
    }, [repoName]);

    return (
        <TrackContext.Provider value={{ container: 'link-repo-dialog' }}>
            <Dialog {...props}>
                <DialogSurface className={appStyles.dialogNarrow}>
                    <DialogBody>
                        <DialogHeader
                            title={intl.formatMessage({
                                defaultMessage: 'Link existing GitHub repository',
                                description: 'Link Existing GitHub repository dialog title.',
                            })}
                        />
                        <DialogContent>
                            <Field
                                className={appStyles.dialogField}
                                required
                                validationMessage={repoNameError}
                                label={intl.formatMessage({
                                    defaultMessage: 'Repository name',
                                    description: 'Label for the the repository name text box.',
                                })}
                            >
                                <Input
                                    value={repoName}
                                    onChange={(_, { value }) => setRepoName(value)}
                                    maxLength={MaxTitleLength}
                                    placeholder={intl.formatMessage({
                                        defaultMessage: 'Repository name (e.g. microsoft/vscode)',
                                        description: 'Placeholder text inside the repository name text box.',
                                    })}
                                />
                            </Field>
                            <Field
                                className={appStyles.dialogField}
                                required
                                label={intl.formatMessage({
                                    defaultMessage: 'Language',
                                    description: 'Label for the language selection dropdown.',
                                })}
                            >
                                <Dropdown
                                    value={getCoursePlatformText(platform)}
                                    selectedOptions={[platform]}
                                    onOptionSelect={(_, data) => setPlatform(data.optionValue as CoursePlatform)}
                                >
                                    <Option value="python">{getCoursePlatformText('python')}</Option>
                                    <Option value="html">{getCoursePlatformText('html')}</Option>
                                </Dropdown>
                            </Field>
                        </DialogContent>
                        <DialogActions>
                            <CancelButton />
                            <ConfirmButton
                                appearance="primary"
                                disabled={isNullOrEmptyString(repoName) || isNonEmptyString(repoNameError)}
                                onClick={() => onLink(repoName.replace(repoNamePrefix, ''), platform)}
                            >
                                <FormattedMessage
                                    description="Link Existing Repo dialog confirm button."
                                    defaultMessage="Link repository"
                                />
                            </ConfirmButton>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </TrackContext.Provider>
    );
});

export default LinkRepoDialog;
